import useSWR from 'swr'
import {
  organizationsApi,
  usersApi,
  type CreditHistoryPage,
  type DailyUsagePage,
  type MemberUsage,
} from '@/lib/api'

/** SWR keys — keep stable so mutate() invalidations work across components. */
export const SWR_KEYS = {
  memberUsage: (orgId: string) => ['org', orgId, 'member-usage'] as const,
  creditHistory: (limit: number, offset: number) =>
    ['users', 'me', 'credit-history', limit, offset] as const,
  dailyUsage: (scope: string, page: number, pageSize: number) =>
    ['users', 'me', 'credit-usage-daily', scope, page, pageSize] as const,
}

export function useMemberUsage(orgId: string | undefined) {
  return useSWR<MemberUsage[]>(
    orgId ? SWR_KEYS.memberUsage(orgId) : null,
    async () => organizationsApi.getMemberUsage(orgId as string),
    // revalidateOnFocus: true so that if a newly-invited member registers while
    // the owner is away, the count updates when the owner returns to the tab.
    { revalidateOnFocus: true },
  )
}

export function useCreditHistory(
  enabled: boolean,
  params: { limit: number; offset: number } = { limit: 25, offset: 0 },
) {
  const { limit, offset } = params
  return useSWR<CreditHistoryPage>(
    enabled ? SWR_KEYS.creditHistory(limit, offset) : null,
    async () => usersApi.getCreditHistory({ limit, offset }),
    { revalidateOnFocus: false, keepPreviousData: true },
  )
}

/** Credit metering: the daily usage log (enrolled accounts), one server page at a time. */
export function useDailyCreditUsage(
  isEnabled: boolean,
  params: { page: number; scope: 'me' | 'team'; pageSize?: number },
) {
  const { page, scope, pageSize = 20 } = params
  return useSWR<DailyUsagePage>(
    isEnabled ? SWR_KEYS.dailyUsage(scope, page, pageSize) : null,
    async () => usersApi.getDailyCreditUsage({ page, pageSize, scope }),
    { revalidateOnFocus: false, keepPreviousData: true },
  )
}
